import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Cable as CableIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { io } from 'socket.io-client';

/**
 * Componente para exibir informações de conexão WebSocket do PrinterBridge
 * Mostra status do serviço, URL de conexão e estatísticas básicas
 */
const WebSocketInfo = ({ customTheme, onAlert }) => {
  // Estados do componente
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Carrega o status da conexão WebSocket
  const loadConnectionStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/printer-bridge/status');
      
      if (response.data) {
        // O backend sempre retorna um objeto ConnectionStatusResponseDto válido
        setConnectionStatus({
          serviceStatus: response.data.serviceStatus || 'offline',
          totalConnections: response.data.totalConnections || 0,
          authenticatedConnections: response.data.authenticatedConnections || 0,
          connections: response.data.connections || [],
          websocketUrl: response.data.websocketUrl || `wss://${window.location.host}/ws-printer`,
          message: response.data.serviceStatus === 'offline' 
            ? 'Nenhuma conexão ativa no momento' 
            : 'Serviço funcionando normalmente',
          timestamp: new Date().toISOString(),
        });
        setLastUpdate(new Date());
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error) {
      console.error('Erro ao carregar status da conexão:', error);
      onAlert?.({
        message: 'Erro ao carregar status do WebSocket: ' + (error.response?.data?.message || error.message),
        severity: 'error'
      });
      
      // Define um status de erro padrão
      setConnectionStatus({
        serviceStatus: 'offline',
        message: 'Erro ao conectar com o serviço',
        totalConnections: 0,
        authenticatedConnections: 0,
        connections: [],
        websocketUrl: `wss://${window.location.host}/ws-printer`,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  // Testa a conectividade WebSocket real
  const testWebSocketConnection = async () => {
    try {
      setTestingConnection(true);
      setTestResult(null);
      
      // Forçar WSS se a página estiver em HTTPS, independentemente do que veio do backend
      const defaultUrl = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws-printer`;
      const websocketUrl = connectionStatus?.websocketUrl || defaultUrl;
      const urlObj = new URL(websocketUrl);
      if (window.location.protocol === 'https:' && urlObj.protocol !== 'wss:') {
        urlObj.protocol = 'wss:';
      }
      const baseUrl = `${urlObj.protocol}//${urlObj.host}`;
      
      onAlert?.({
        message: 'Iniciando teste de conectividade WebSocket...',
        severity: 'info'
      });

      // Criar conexão Socket.IO diretamente ao namespace /ws-printer
      const printerSocket = io(`${baseUrl}/ws-printer`, {
        path: '/socket.io/',
        transports: ['websocket'],
        timeout: 10000,
        forceNew: true,
        autoConnect: false,
      });

      let testTimeout;
      let connectionEstablished = false;

      const testPromise = new Promise((resolve, reject) => {
        // Timeout para o teste (15 segundos)
        testTimeout = setTimeout(() => {
          reject(new Error('Timeout: Teste de conectividade excedeu 15 segundos'));
        }, 15000);

        // Evento de conexão estabelecida
        printerSocket.on('connect', () => {
          connectionEstablished = true;
          console.log('🔌 Conexão WebSocket estabelecida para teste');
        });

        // Evento específico do PrinterBridge quando conexão é estabelecida
        printerSocket.on('connection:established', (data) => {
          console.log('✅ Evento connection:established recebido:', data);
          
          clearTimeout(testTimeout);
          printerSocket.disconnect();
          
          resolve({
            success: true,
            message: 'Conexão WebSocket estabelecida com sucesso',
            details: {
              clientId: data.clientId,
              timestamp: data.timestamp,
              serverMessage: data.message
            }
          });
        });

        // Eventos de erro
        printerSocket.on('connect_error', (error) => {
          console.error('❌ Erro de conexão WebSocket:', error);
          clearTimeout(testTimeout);
          reject(new Error(`Erro de conexão: ${error.message}`));
        });

        printerSocket.on('disconnect', (reason) => {
          console.log('🔌 Desconectado do WebSocket:', reason);
          if (!connectionEstablished) {
            clearTimeout(testTimeout);
            reject(new Error(`Desconectado antes da autenticação: ${reason}`));
          }
        });

        // Iniciar conexão
        printerSocket.connect();
      });

      const result = await testPromise;
      
      setTestResult(result);
      onAlert?.({
        message: result.message,
        severity: 'success'
      });

    } catch (error) {
      console.error('Erro no teste de conectividade:', error);
      
      const errorResult = {
        success: false,
        message: 'Falha na conectividade WebSocket',
        error: error.message,
        details: {
          timestamp: new Date().toISOString()
        }
      };
      
      setTestResult(errorResult);
      onAlert?.({
        message: `Teste de conectividade falhou: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  // Carrega o status na inicialização
  useEffect(() => {
    loadConnectionStatus();
  }, []);

  // Copia texto para a área de transferência
  const handleCopyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      onAlert?.({
        message: `${label} copiado para a área de transferência`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao copiar:', error);
      onAlert?.({
        message: 'Erro ao copiar para a área de transferência',
        severity: 'error'
      });
    }
  };

  // Determina o status visual baseado no estado do serviço
  const getStatusInfo = () => {
    if (!connectionStatus) {
      return {
        icon: <WifiOffIcon />,
        color: '#9e9e9e',
        label: 'Carregando...',
        severity: 'info'
      };
    }

         switch (connectionStatus.serviceStatus) {
       case 'online':
         return {
           icon: <CheckCircleIcon />,
           color: '#4caf50',
           label: 'Online',
           severity: 'success'
         };
       case 'degraded':
         return {
           icon: <WarningIcon />,
           color: '#ff9800',
           label: 'Degradado',
           severity: 'warning'
         };
       case 'offline':
       default:
         return {
           icon: <ErrorIcon />,
           color: '#f44336',
           label: 'Offline',
           severity: 'error'
         };
     }
  };

  // Formata data para exibição
  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const statusInfo = getStatusInfo();
  const websocketUrl = connectionStatus?.websocketUrl || `wss://${window.location.host}/ws-printer`;

  return (
    <Box>
      {/* Cabeçalho da seção */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WifiIcon sx={{ mr: 1, color: customTheme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>
            Informações de Conexão WebSocket
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={testingConnection ? <CircularProgress size={16} /> : <CableIcon />}
            onClick={testWebSocketConnection}
            disabled={testingConnection || loading}
            size="small"
            color="secondary"
          >
            {testingConnection ? 'Testando...' : 'Testar Conexão'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={loadConnectionStatus}
            disabled={loading}
            size="small"
          >
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </Box>
      </Box>

      {/* Resultado do Teste de Conectividade */}
      {testResult && (
        <Card sx={{ 
          mb: 3,
          border: '1px solid',
          borderColor: testResult.success 
            ? alpha(customTheme.palette.success.main, 0.3)
            : alpha(customTheme.palette.error.main, 0.3),
          bgcolor: testResult.success 
            ? alpha(customTheme.palette.success.main, 0.05)
            : alpha(customTheme.palette.error.main, 0.05),
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                color: testResult.success 
                  ? customTheme.palette.success.main 
                  : customTheme.palette.error.main, 
                mr: 1 
              }}>
                {testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Resultado do Teste de Conectividade
              </Typography>
              <Chip
                label={testResult.success ? 'Sucesso' : 'Falha'}
                color={testResult.success ? 'success' : 'error'}
                size="small"
                sx={{ ml: 2 }}
                icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {testResult.message}
            </Typography>
            
            {testResult.error && (
              <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                <strong>Erro:</strong> {testResult.error}
              </Typography>
            )}
            
            {testResult.details && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Detalhes:</strong>
                </Typography>
                {testResult.details.clientId && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    • ID do Cliente: {testResult.details.clientId}
                  </Typography>
                )}
                {testResult.details.serverMessage && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    • Mensagem do Servidor: {testResult.details.serverMessage}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  • Timestamp: {formatDate(testResult.details.timestamp)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Status do Serviço */}
      <Card sx={{ 
        mb: 3,
        border: '1px solid',
        borderColor: alpha(statusInfo.color, 0.3),
        bgcolor: alpha(statusInfo.color, 0.05),
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ color: statusInfo.color, mr: 1 }}>
              {statusInfo.icon}
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: statusInfo.color }}>
              Status do Serviço: {statusInfo.label}
            </Typography>
            <Chip
              label={statusInfo.label}
              color={statusInfo.severity}
              size="small"
              sx={{ ml: 2 }}
              icon={statusInfo.icon}
            />
          </Box>
          
          {connectionStatus?.message && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {connectionStatus.message}
            </Typography>
          )}
          
          {lastUpdate && (
            <Typography variant="caption" color="text.secondary">
              Última atualização: {formatDate(lastUpdate.toISOString())}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* URL de Conexão */}
      <Card sx={{ 
        mb: 3,
        border: '1px solid',
        borderColor: alpha(customTheme.palette.primary.main, 0.2),
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoIcon sx={{ mr: 1, color: customTheme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              URL de Conexão WebSocket
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: 2,
            bgcolor: alpha(customTheme.palette.primary.main, 0.05),
            borderRadius: 1,
            border: '1px solid',
            borderColor: alpha(customTheme.palette.primary.main, 0.2),
          }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                flex: 1,
                fontWeight: 500,
              }}
            >
              {websocketUrl}
            </Typography>
            <Tooltip title="Copiar URL">
              <IconButton
                size="small"
                onClick={() => handleCopyToClipboard(websocketUrl, 'URL do WebSocket')}
                sx={{ color: customTheme.palette.primary.main }}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Use esta URL no Printer Bridge para conectar ao sistema. 
              Certifique-se de que o certificado SSL seja válido para conexões seguras (WSS).
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Estatísticas de Conexão */}
      {connectionStatus && (
        <Card sx={{ 
          border: '1px solid',
          borderColor: alpha(customTheme.palette.divider, 0.2),
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <WifiIcon sx={{ mr: 1, color: customTheme.palette.primary.main }} />
              <Typography variant="h6" fontWeight={600}>
                Estatísticas de Conexão
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 3 
            }}>
                             {/* Conexões Autenticadas */}
               <Box sx={{ textAlign: 'center' }}>
                 <Typography variant="h4" fontWeight={600} color="primary.main">
                   {connectionStatus.authenticatedConnections || 0}
                 </Typography>
                 <Typography variant="body2" color="text.secondary">
                   Conexões Autenticadas
                 </Typography>
               </Box>
               
               {/* Total de Conexões */}
               <Box sx={{ textAlign: 'center' }}>
                 <Typography variant="h4" fontWeight={600} color="text.primary">
                   {connectionStatus.totalConnections || 0}
                 </Typography>
                 <Typography variant="body2" color="text.secondary">
                   Total de Conexões
                 </Typography>
               </Box>
               
               {/* Status do Serviço */}
               <Box sx={{ textAlign: 'center' }}>
                 <Typography variant="body1" fontWeight={600} color="text.primary">
                   {connectionStatus.serviceStatus === 'online' ? 'Online' : 
                    connectionStatus.serviceStatus === 'degraded' ? 'Degradado' : 'Offline'}
                 </Typography>
                 <Typography variant="body2" color="text.secondary">
                   Status do Serviço
                 </Typography>
               </Box>
            </Box>
            
                         {connectionStatus.connections && connectionStatus.connections.length > 0 && (
               <Box sx={{ mt: 3 }}>
                 <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                   Conexões Ativas Detalhadas:
                 </Typography>
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                   {connectionStatus.connections.map((conn, index) => (
                     <Box 
                       key={index}
                       sx={{ 
                         p: 2,
                         bgcolor: alpha(customTheme.palette.success.main, 0.05),
                         borderRadius: 1,
                         border: '1px solid',
                         borderColor: alpha(customTheme.palette.success.main, 0.2),
                       }}
                     >
                       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                         <Typography variant="body2" fontWeight={500}>
                           Cliente: {conn.clientId || 'ID não disponível'}
                         </Typography>
                         <Chip
                           label={conn.authenticated ? "Autenticado" : "Não Autenticado"}
                           color={conn.authenticated ? "success" : "warning"}
                           size="small"
                           icon={conn.authenticated ? <CheckCircleIcon /> : <WarningIcon />}
                         />
                       </Box>
                       <Typography variant="caption" color="text.secondary">
                         Conectado em: {formatDate(conn.connectedAt)}
                       </Typography>
                       <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                         • Status: {conn.healthy ? 'Saudável' : 'Com problemas'}
                       </Typography>
                     </Box>
                   ))}
                 </Box>
               </Box>
             )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default WebSocketInfo; 